import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./PatientSelector.css";
import logo2 from "./logo2.png";
import { FaVideo, FaExclamationTriangle } from "react-icons/fa";
import app from "./firebase";

const patients = [
  {
    mrn: "11223344",
    name: "Luke Mason",
    age: 87,
    sex: "M",
    facility: "CGH",
    unit: "3E",
    room: "331",
    dx: "Fracture of Right Hip",
    unit_id: "66094",
  },
  {
    mrn: "55667788",
    name: "Mary Smith",
    age: 74,
    sex: "F",
    facility: "CGH",
    unit: "2E",
    room: "228",
    dx: "CHF with fluid overload",
    unit_id: "66179",
  },
  {
    mrn: "99001122",
    name: "David Chen",
    age: 81,
    sex: "M",
    facility: "CGH",
    unit: "1W",
    room: "145",
    dx: "Sepsis - UTI",
    unit_id: "66184",
  },
  {
    mrn: "-",
    name: "-",
    age: "N",
    sex: "A",
    facility: "-",
    unit: "-",
    room: "-",
    dx: "-",
    unit_id: "67189",
  },
];

const PatientSelector = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("Epic"); // Epic or eCM

  const PROXY_URL = "https://proxy.vergenursing.com/api";
  const [alerts, setAlerts] = useState({});

  // Reset clearing on reload & set default demo alerts
  useEffect(() => {
    // Default alert simulation for demo
    const defaultAlerts = {
      "66094": true,
      "66179": false,
      "66184": false,
    };

    // Get any "cleared alerts" saved in memory
    const cleared = window.clearedAlerts || {};

    // Build merged alert list
    const merged = {};
    Object.keys(defaultAlerts).forEach((unitId) => {
      merged[unitId] = cleared[unitId] ? false : defaultAlerts[unitId];
    });

    setAlerts(merged);
  }, []);

  const handleNavigateToReports = () => navigate("/outpatient-schedule");
  const handleNavigateToMonitor = () => navigate("/video-wall");
  const handleNavigateToDashboard = () => navigate("/patient-dashboard");

  const filtered = patients.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.mrn.includes(search) ||
      p.room.includes(search)
  );

  // ------------------------------
  // 🔥 HANDLE CALL START (eCM only)
  // ------------------------------
  const handleStartCall = async (patient) => {
    const { unit_id } = patient;

    try {
      const response = await fetch(`${PROXY_URL}/initialize_call`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unit_id }),
      });

      const data = await response.json();
      console.log("🔍 Proxy Result:", data);

      const callToken = data.call_token || "NO_TOKEN";

      const connectUrl =
        `https://connectapigweproxy.caregility.net/api/call?` +
        `call_token=${callToken}` +
        `&c_id=4bdCVTFNVKs` +
        `&dr_name=Virtual+Nurse` +
        `&uid=12345` +
        `&privacy=true`;

      // ----- Popup Window -----
      const popup = window.open("", "_blank", "width=1200,height=800");

      popup.document.write(`
        <html>
        <head>
          <title>${patient.name} - Virtual Call</title>
          <style>
            body { margin:0; font-family:Arial, sans-serif; }
            .header {
              background:#1e3a5f; color:white; padding:12px 16px;
              display:flex; align-items:center; justify-content:space-between;
            }
            .patient-info { font-size:16px; font-weight:500; }
            .disconnect-btn {
              background:#d9534f; color:white; padding:8px 16px;
              border:none; border-radius:6px; font-size:15px;
              cursor:pointer;
            }
            iframe {
              width:100%; height:calc(100vh - 60px); border:none;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="patient-info">
              <div><strong>${patient.name}</strong></div>
              <div>Room ${patient.room} • ${patient.age} / ${patient.sex} • ${patient.dx}</div>
            </div>
            <button class="disconnect-btn" onclick="disconnectCall()">Disconnect</button>
          </div>

          <iframe src="${connectUrl}" allow="camera *; microphone *"></iframe>

          <script>
            function disconnectCall() {
              fetch("https://proxy.vergenursing.com/api/end_call", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  call_token: "${callToken}",
                  uid: "12345"
                })
                }).then(r => {
                window.close();
              });
            }
          </script>

        </body>
        </html>
      `);
    } catch (error) {
      console.error("❌ Error:", error);
    }
  };

  return (
    <div className="patient-select-wrapper">
      <div className="patient-select-header">
        <img src={logo2} className="ps-logo" alt="logo" />
        <h1>Patient Census</h1>

        {/* Mode Toggle */}
        <div className="mode-toggle">
          <button
            className={`toggle-btn ${mode === "Epic" ? "active" : ""}`}
            onClick={() => setMode("Epic")}
          >
            EMR
          </button>
          <button
            className={`toggle-btn ${mode === "eCM" ? "active" : ""}`}
            onClick={() => setMode("eCM")}
          >
            CDS
          </button>
        </div>
      </div>

      {/* Top Nav */}
      <div className="top-nav-buttons">
        <button onClick={handleNavigateToDashboard} className="top-nav-btn">
          Patient Dashboard
        </button>
        <button onClick={handleNavigateToMonitor} className="top-nav-btn">
          Monitor Rooms
        </button>
        <button onClick={handleNavigateToReports} className="top-nav-btn">
          Outpatient Schedule
        </button>
        </div>

      {/* Search */}
      <input
        placeholder="Search patients by name, MRN, or room..."
        className="ps-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Table */}
      <div className="ps-table">
        <div className="ps-table-header">
          <span></span> {/* Camera/Alert column */}
          <span>Name</span>
          <span>MRN</span>
          <span>Age / Sex</span>
          <span>Facility</span>
          <span>Unit</span>
          <span>Room</span>
          <span>Diagnosis</span>
        </div>

        {filtered.map((p) => (
          <div
            key={p.mrn}
            onClick={() => {
              // 🚫 Don't navigate for the "-" placeholder row
              if (!p.room || p.room === "-") return;

              // ✅ Navigate to room-specific page, e.g. /navigator-331
              navigate(`/navigator-${p.room}`);
            }}
            className="ps-row"
          >
            {/* 1st Column — camera OR alert */}
            <span className="alert-or-camera-col">
              {mode === "eCM" && (
                <span
                  className="camera-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartCall(p);
                  }}
                >
                  <FaVideo />
                </span>
              )}

              {mode === "Epic" && alerts[p.unit_id] && (
                <span className="alert-icon">
                  <FaExclamationTriangle />
                </span>
              )}
            </span>

            <span className="ps-name">{p.name}</span>
            <span>{p.mrn}</span>
            <span>{p.age} / {p.sex}</span>
            <span>{p.facility}</span>
            <span>{p.unit}</span>
            <span>{p.room}</span>
            <span className="ps-dx">{p.dx}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientSelector;
